import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import axios from "axios";
import { API_URL } from "../../config/apiConfig";
import { FaShieldAlt, FaCheck } from "react-icons/fa";

const RolePermissions = () => {
  const { user } = useSelector((state) => state.auth);
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [allowedSidebars, setAllowedSidebars] = useState([]);
  const [savingPermissions, setSavingPermissions] = useState(false);

  // Get auth headers
  const getHeaders = () => {
    const token = localStorage.getItem("token") || (user && user.token);
    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  // Sidebar options matching layouts/AdminLayout.jsx
  const sidebarOptions = [
    { path: "/admin/dashboard", name: "Dashboard" },
    { path: "/admin/roles", name: "Role Management" },
    { path: "/admin/role-permissions", name: "Role Permissions" },
    { path: "/admin/users", name: "Users" },
    { path: "/admin/bookings", name: "Bookings" },
    { path: "/admin/abandoned-bookings", name: "Booking Inquiries" },
    { path: "/admin/live-tracking", name: "Live Tracking" },
    { path: "/admin/ride-issues", name: "Ride Issues" },
    { path: "/admin/corporates", name: "Corporate" },
    { path: "/admin/corporate-wallet", name: "Corporate Wallet" },
    { path: "/admin/cabs", name: "Cab Management" },
    { path: "/admin/drivers", name: "Driver Management" },
    { path: "/admin/vendors", name: "Vendor Management" },
    { path: "/admin/commission", name: "Commission Management" },
    { path: "/admin/airports", name: "Airport Management" },
    { path: "/admin/rental-packages", name: "Rental Packages" },
    { path: "/admin/coupons", name: "Coupon Management" },
    { path: "/admin/wallet/summary", name: "Wallet Summary" },
    { path: "/admin/wallet/drivers", name: "Driver Wallets" },
    { path: "/admin/wallet/withdrawals", name: "Pending Withdrawals" },
    { path: "/admin/wallet/user-withdrawals", name: "User Withdrawals" },
    { path: "/admin/popular-routes", name: "Fixed Routes" },
    { path: "/admin/popular-cities", name: "Popular Cities" },
    { path: "/admin/footer-routes", name: "Premium Routes" },
    { path: "/admin/document-settings", name: "Document Requirements" },
    { path: "/admin/booking-settings", name: "Booking Settings" },
    { path: "/admin/app-version", name: "App Version" },
    { path: "/admin/testimonials", name: "Testimonials" },
    { path: "/admin/media", name: "Media & News" },
    { path: "/admin/ads", name: "Ads Management" },
    { path: "/admin/blogs", name: "Blog Management" },
    { path: "/admin/support-details", name: "Support Details" },
    { path: "/admin/support-messages", name: "Support Messages" },
    { path: "/admin/maintenance", name: "Maintenance Mode" },
    { path: "/admin/auth-images", name: "Auth Page Images" },
  ];

  // Load roles
  const loadRoles = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/roles`, getHeaders());
      if (res.data.success) {
        setRoles(res.data.data);
        if (res.data.data.length > 0 && !selectedRole) {
          setSelectedRole(res.data.data[0].name);
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to fetch roles");
    }
  };

  // Load role permissions
  const loadRolePermissions = async () => {
    if (!selectedRole) return;
    try {
      const res = await axios.get(`${API_URL}/admin/role-permissions/${selectedRole}`, getHeaders());
      if (res.data.success && res.data.data) {
        setAllowedSidebars(res.data.data.allowedSidebars || []);
      } else {
        setAllowedSidebars([]);
      }
    } catch (err) {
      setAllowedSidebars([]);
    }
  };

  useEffect(() => {
    loadRoles();
  }, []);

  useEffect(() => {
    loadRolePermissions();
  }, [selectedRole]);

  // Toggle sidebar item checkbox
  const handleCheckboxChange = (path) => {
    if (allowedSidebars.includes(path)) {
      setAllowedSidebars(allowedSidebars.filter((p) => p !== path));
    } else {
      setAllowedSidebars([...allowedSidebars, path]);
    }
  };

  // Save Permissions
  const handleSavePermissions = async () => {
    if (!selectedRole) {
      toast.error("Please select a role first");
      return;
    }
    
    setSavingPermissions(true);
    try {
      const res = await axios.post(
        `${API_URL}/admin/role-permissions`,
        {
          roleName: selectedRole,
          allowedSidebars: allowedSidebars,
        },
        getHeaders()
      );
      if (res.data.success) {
        toast.success("Role permissions updated successfully!");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save permissions");
    } finally {
      setSavingPermissions(false);
    }
  };

  // Select all permissions
  const handleSelectAll = () => {
    setAllowedSidebars(sidebarOptions.map((opt) => opt.path));
  };

  // Deselect all permissions
  const handleDeselectAll = () => {
    setAllowedSidebars([]);
  };

  return (
    <div className="w-full py-8">
      <div className="px-4 sm:px-6 md:px-8 max-w-full mx-auto">
        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-100 bg-gray-50/50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <label className="text-sm font-bold text-gray-700 whitespace-nowrap">Configure Permissions For:</label>
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-bold text-gray-700 bg-white focus:outline-none focus:border-blue-500"
              >
                <option value="" disabled>Select a role...</option>
                <option value="admin">Default Admin</option>
                {roles.map((r) => (
                  <option key={r._id} value={r.name}>
                    {r.name.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
            
            {selectedRole && (
              <div className="flex gap-2">
                <button
                  onClick={handleSelectAll}
                  className="px-4 py-2 border border-gray-200 hover:border-blue-500 hover:text-blue-600 rounded-xl text-xs font-bold text-gray-500 transition-colors bg-white"
                >
                  Select All
                </button>
                <button
                  onClick={handleDeselectAll}
                  className="px-4 py-2 border border-gray-200 hover:border-red-500 hover:text-red-600 rounded-xl text-xs font-bold text-gray-500 transition-colors bg-white"
                >
                  Deselect All
                </button>
              </div>
            )}
          </div>

          {selectedRole ? (
            <div className="p-6">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-6">
                Select Sidebar Modules for Role: <span className="text-blue-600 font-extrabold">{selectedRole.toUpperCase()}</span>
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                {sidebarOptions.map((opt) => {
                  const isChecked = allowedSidebars.includes(opt.path);
                  return (
                    <div
                      key={opt.path}
                      onClick={() => handleCheckboxChange(opt.path)}
                      className={`flex items-center justify-between p-4 border rounded-2xl cursor-pointer select-none transition-all duration-200 ${
                        isChecked
                          ? "border-blue-500 bg-blue-50/30 text-blue-900 shadow-sm"
                          : "border-gray-100 hover:border-gray-200 hover:bg-gray-50/50 text-gray-600"
                      }`}
                    >
                      <div className="flex flex-col">
                        <span className="text-sm font-bold">{opt.name}</span>
                        <span className="text-[10px] text-gray-400 font-medium tracking-wide">{opt.path}</span>
                      </div>
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center border transition-all duration-200 ${
                          isChecked
                            ? "bg-blue-600 border-blue-600 text-white"
                            : "border-gray-200 bg-white"
                        }`}
                      >
                        {isChecked && <FaCheck className="text-[10px]" />}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-end pt-5 border-t border-gray-100">
                <button
                  onClick={handleSavePermissions}
                  disabled={savingPermissions}
                  className="px-8 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm tracking-wide shadow-lg shadow-blue-200 transition-all duration-200 active:scale-[0.98] disabled:opacity-50"
                >
                  {savingPermissions ? "SAVING..." : "SAVE PERMISSIONS"}
                </button>
              </div>
            </div>
          ) : (
            <div className="py-16 text-center text-gray-400 italic text-sm">
              Please select a role from the dropdown above to configure its permissions.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RolePermissions;
